import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { CONSENT_KEY, getCookieConsent } from '../components/CookieConsent';

const LAST_UPDATED = '2 September 2026';

const COOKIES = [
  { name: 'access_token', type: 'Essential', purpose: 'Keeps editors securely signed in to the I DO admin area. Only set when a member of our team logs in.', duration: 'Session / up to 7 days' },
  { name: 'ido_cookie_consent', type: 'Preference', purpose: 'Remembers whether you accepted or declined optional cookies so we do not ask again.', duration: '12 months (browser storage)' },
  { name: 'Third-party media', type: 'Optional', purpose: 'Images and video embedded from providers such as Instagram, Unsplash or Pexels may set their own cookies when loaded.', duration: 'Set by the provider' },
];

const Section = ({ title, children, id }) => (
  <section id={id} className="mb-12" data-testid={`privacy-section-${id}`}>
    <h2
      className="text-2xl md:text-3xl mb-4 tracking-tight"
      style={{ fontFamily: '"Playfair Display", serif', color: '#2C2C2C' }}
    >
      {title}
    </h2>
    <div className="space-y-4 text-base leading-relaxed" style={{ color: '#5A5A5A' }}>
      {children}
    </div>
  </section>
);

const CookieTable = () => (
  <div className="overflow-x-auto rounded-xl border border-[#EFE6E9] bg-white">
    <table className="w-full text-sm" data-testid="cookie-table">
      <thead>
        <tr style={{ backgroundColor: '#DE92A9' }} className="text-white text-left text-[10px] tracking-[0.18em] uppercase">
          <th className="px-4 py-3 font-semibold">Name</th>
          <th className="px-4 py-3 font-semibold">Type</th>
          <th className="px-4 py-3 font-semibold">Purpose</th>
          <th className="px-4 py-3 font-semibold">Duration</th>
        </tr>
      </thead>
      <tbody>
        {COOKIES.map((c) => (
          <tr key={c.name} className="border-t border-[#F3ECEF] align-top">
            <td className="px-4 py-3 font-mono text-xs text-[#2C2C2C]">{c.name}</td>
            <td className="px-4 py-3 text-[#C9758F] font-medium">{c.type}</td>
            <td className="px-4 py-3 text-[#5A5A5A]">{c.purpose}</td>
            <td className="px-4 py-3 text-[#757B7F] whitespace-nowrap">{c.duration}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ConsentStatus = () => {
  const [consent, setConsent] = useState(getCookieConsent());

  const reset = () => {
    window.localStorage.removeItem(CONSENT_KEY);
    setConsent(null);
    toast.success('Cookie preferences cleared. Reload the page to choose again.');
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-4 rounded-xl border border-[#F0DDE3] bg-[#FBF3F5] p-5" data-testid="cookie-preferences-box">
      <Cookie size={22} style={{ color: '#DE92A9' }} className="shrink-0" />
      <p className="text-sm flex-1" style={{ color: '#2C2C2C' }} data-testid="cookie-consent-status">
        {consent
          ? `Your current choice: ${consent.status === 'accepted' ? 'all cookies accepted' : 'essential cookies only'}.`
          : 'You have not made a cookie choice yet. The banner will appear on your next visit.'}
      </p>
      {consent && (
        <button
          type="button"
          onClick={reset}
          className="inline-flex items-center gap-2 text-xs tracking-[0.15em] font-semibold px-5 py-2.5 rounded-full border-2 transition-colors hover:bg-white"
          style={{ borderColor: '#DE92A9', color: '#C9758F' }}
          data-testid="cookie-reset-btn"
        >
          <RotateCcw size={13} />
          CHANGE MY CHOICE
        </button>
      )}
    </div>
  );
};

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF9F7' }} data-testid="privacy-policy-page">
      <SEO
        title="Privacy & Cookie Policy"
        description="How I DO Bridal Magazine Malta collects, uses and protects your personal data, and the cookies used on idobridalmagazinemalta.com."
        keywords="privacy policy, cookie policy, I DO Bridal Magazine Malta, GDPR Malta"
        breadcrumbs={[{ name: 'Privacy & Cookie Policy', url: '/privacy-policy' }]}
      />

      <div className="pt-20 pb-16 text-center px-6" style={{ backgroundColor: '#fff' }}>
        <p className="text-xs tracking-[0.3em] uppercase mb-4" style={{ color: '#DE92A9' }}>Legal</p>
        <h1
          className="text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-4"
          style={{ fontFamily: '"Playfair Display", serif', color: '#2C2C2C' }}
          data-testid="privacy-heading"
        >
          Privacy & Cookie Policy
        </h1>
        <p className="text-sm" style={{ color: '#757B7F' }}>Last updated: {LAST_UPDATED}</p>
      </div>

      <div className="max-w-3xl mx-auto px-6 lg:px-8 py-16">
        <Section id="who-we-are" title="Who we are">
          <p>
            I DO Bridal Magazine Malta ("I DO", "we", "us") is published by Standard Publications Ltd, Standard House,
            Birkirkara Hill, St. Julian's STJ 1149, Malta. We are the data controller for personal data collected
            through <strong>idobridalmagazinemalta.com</strong> (the "Site"). You can reach us at{' '}
            <a href="mailto:idobridalmagazinemalta@gmail.com" className="underline" style={{ color: '#DE92A9' }}>idobridalmagazinemalta@gmail.com</a>{' '}
            or on +356 9980 1504.
          </p>
        </Section>

        <Section id="data-we-collect" title="The information we collect">
          <p>We only collect personal data that you choose to share with us:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Newsletter subscriptions</strong>: your email address.</li>
            <li><strong>Contact and advertising enquiries</strong>: your name, email address and the content of your message.</li>
            <li><strong>Real wedding submissions</strong>: the couple's names, wedding date and venue, your contact details, the story you write, supplier credits and the photographs or links you provide.</li>
            <li><strong>Technical data</strong>: standard server logs (IP address, browser type, pages requested) needed to deliver and secure the Site.</li>
          </ul>
        </Section>

        <Section id="how-we-use" title="How we use your information">
          <ul className="list-disc pl-6 space-y-2">
            <li>To send you the I DO newsletter when you have subscribed (legal basis: consent, which you may withdraw at any time via the unsubscribe link or by emailing us).</li>
            <li>To respond to your enquiries and advertising requests (legal basis: our legitimate interest in running the magazine and pre-contractual steps).</li>
            <li>To review, edit and publish real wedding stories you submit, in print and online (legal basis: consent and performance of our agreement with you).</li>
            <li>To keep the Site secure, prevent abuse and understand which pages are popular (legal basis: legitimate interest).</li>
          </ul>
          <p>We never sell your personal data, and we do not use it for automated decision-making.</p>
        </Section>

        <Section id="sharing" title="Who we share it with">
          <p>
            We share data only with trusted service providers who process it on our behalf and under contract: our
            hosting and infrastructure provider, our email delivery provider used to send newsletters and
            confirmations, and, for published wedding stories, the photographers and suppliers you credit. Where a
            provider is located outside the European Economic Area, we rely on EU Standard Contractual Clauses or an
            equivalent safeguard.
          </p>
        </Section>

        <Section id="retention" title="How long we keep it">
          <ul className="list-disc pl-6 space-y-2">
            <li>Newsletter data: until you unsubscribe, then removed from our active list.</li>
            <li>Enquiries: up to 24 months after our last exchange.</li>
            <li>Published wedding stories: for as long as the story remains on the Site, unless you ask us to take it down.</li>
            <li>Server logs: typically 30 days.</li>
          </ul>
        </Section>

        <Section id="cookies" title="Cookies and similar technologies">
          <p>
            Cookies are small files stored on your device. We use a very small number of them, and we ask for your
            permission before setting anything that is not strictly necessary.
          </p>
          <CookieTable />
          <p>
            You can also control cookies through your browser settings. Blocking essential cookies may prevent parts of
            the Site (such as the editor login) from working.
          </p>
          <ConsentStatus />
        </Section>

        <Section id="your-rights" title="Your rights">
          <p>Under the EU General Data Protection Regulation (GDPR) and Malta's Data Protection Act, you have the right to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>access the personal data we hold about you and receive a copy;</li>
            <li>ask us to correct inaccurate or incomplete data;</li>
            <li>ask us to erase your data or restrict how we use it;</li>
            <li>object to processing based on legitimate interest, and to direct marketing at any time;</li>
            <li>withdraw consent at any time, without affecting processing that has already taken place;</li>
            <li>receive your data in a portable format.</li>
          </ul>
          <p>
            To exercise any of these rights, email{' '}
            <a href="mailto:idobridalmagazinemalta@gmail.com" className="underline" style={{ color: '#DE92A9' }}>idobridalmagazinemalta@gmail.com</a>.
            We will respond within one month. If you are unhappy with our response you may lodge a complaint with the
            Office of the Information and Data Protection Commissioner (IDPC), Malta, at idpc.org.mt.
          </p>
        </Section>

        <Section id="children" title="Children">
          <p>The Site is intended for adults planning a wedding. We do not knowingly collect personal data from anyone under 16.</p>
        </Section>

        <Section id="changes" title="Changes to this policy">
          <p>
            We may update this policy from time to time. The "last updated" date at the top of this page tells you when
            it last changed, and material changes will be highlighted on the Site.
          </p>
        </Section>

        <div className="text-center pt-6 border-t border-[#EFE6E9]">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 text-xs tracking-[0.2em] font-semibold px-8 py-4 text-white rounded-full transition-all hover:tracking-[0.25em]"
            style={{ backgroundColor: '#DE92A9' }}
            data-testid="privacy-contact-btn"
          >
            QUESTIONS? CONTACT US
          </Link>
        </div>
      </div>
    </div>
  );
}
